import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from "@mui/icons-material";
import { formatNumber } from "../utils/formatNumber";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const WHEEL_STEP = 0.2;

export default function ImageModal({ open, onClose, imageDetail, type }) {
  const images = useMemo(() => imageDetail?.images || [], [imageDetail]);
  const [current, setCurrent] = useState(images[0] || null);

  useEffect(() => {
    if (open) setCurrent(images[0] || null);
  }, [open, images]);

  // Handle Previous/Next
  const handlePrev = useCallback(() => {
    if (!current || images.length <= 1) return;
    const idx = images.findIndex((img) => img.id === current.id);
    if (idx > 0) {
      setCurrent(images[idx - 1]);
    } else {
      setCurrent(images[images.length - 1]); // Loop to end
    }
  }, [current, images]);

  const handleNext = useCallback(() => {
    if (!current || images.length <= 1) return;
    const idx = images.findIndex((img) => img.id === current.id);
    if (idx < images.length - 1) {
      setCurrent(images[idx + 1]);
    } else {
      setCurrent(images[0]); // Loop to start
    }
  }, [current, images]);

  // Arrow keys support
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, handlePrev, handleNext, onClose]);

  // ====== Zoom / Pan State ======
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Drag state (single pointer)
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const lastPosRef = useRef({ x: 0, y: 0 });

  // Pinch state (two pointers)
  const pointersRef = useRef(new Map()); // pointerId -> {x,y}
  const isPinchingRef = useRef(false);
  const pinchStartDistRef = useRef(0);
  const pinchStartScaleRef = useRef(1);
  const pinchCenterRef = useRef({ x: 0, y: 0 });

  // ฟังก์ชันล้างสถานะทั้งหมด (เรียกก่อนปิด)
  const resetViewerState = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    isDraggingRef.current = false;
    setIsDragging(false);
    isPinchingRef.current = false;
    pinchStartDistRef.current = 0;
    pinchStartScaleRef.current = 1;
    pinchCenterRef.current = { x: 0, y: 0 };
    pointersRef.current.clear();
    setCurrent(null);
  }, []);

  // reset เมื่อเปลี่ยนรูป
  useEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    isDraggingRef.current = false;
    isPinchingRef.current = false;
    setIsDragging(false);
    pointersRef.current.clear();
  }, [current]);

  // ====== ปิด native drag & drop ทั้งหน้าต่างตอน modal เปิด ======
  useEffect(() => {
    if (!open) return;
    const stop = (e) => {
      e.preventDefault();
    };
    window.addEventListener("dragstart", stop, { passive: false });
    window.addEventListener("dragover", stop, { passive: false });
    window.addEventListener("drop", stop, { passive: false });
    return () => {
      window.removeEventListener("dragstart", stop);
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
    };
  }, [open]);

  // ✅ handler ปิด: ล้างก่อนแล้วค่อยปิด
  const handleClose = useCallback(() => {
    resetViewerState();
    onClose?.();
  }, [resetViewerState, onClose]);

  // (ตัวเลือก) ถ้าปิด modal จากภายนอก ให้ล้างด้วยเสมอ
  useEffect(() => {
    if (!open) resetViewerState();
  }, [open, resetViewerState]);

  // ====== Utils ======
  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const datePart = d.toLocaleDateString("th-TH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timePart = d.toLocaleTimeString("th-TH", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${datePart} ${timePart}`;
  };

  const formatDateFull = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const getDistance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const getMidpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  // ====== Wheel zoom (เดสก์ท็อป) ======
  const handleWheel = useCallback(
    (e) => {
      if (!containerRef.current) return;
      e.preventDefault();

      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const nextScale = clamp(
        e.deltaY < 0 ? scale + WHEEL_STEP : scale - WHEEL_STEP,
        MIN_SCALE,
        MAX_SCALE,
      );

      if (nextScale === MIN_SCALE) {
        setScale(MIN_SCALE);
        setOffset({ x: 0, y: 0 });
        return;
      }

      const scaleFactor = nextScale / scale;
      setOffset({
        x: mouseX - (mouseX - offset.x) * scaleFactor,
        y: mouseY - (mouseY - offset.y) * scaleFactor,
      });
      setScale(nextScale);
    },
    [scale, offset],
  );

  // ====== Pointer helpers ======
  const updatePointer = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    const x = e.clientX - (rect?.left ?? 0);
    const y = e.clientY - (rect?.top ?? 0);
    pointersRef.current.set(e.pointerId, { x, y });
    return { x, y };
  };
  const removePointer = (e) => {
    pointersRef.current.delete(e.pointerId);
  };

  // ====== Pointer Events ======
  const onPointerDown = useCallback(
    (e) => {
      e.currentTarget.setPointerCapture?.(e.pointerId);
      updatePointer(e);

      const activeCount = pointersRef.current.size;

      if (activeCount === 1 && scale > 1 && !isPinchingRef.current) {
        isDraggingRef.current = true;
        setIsDragging(true);
        lastPosRef.current = { x: e.clientX, y: e.clientY };
      }

      if (activeCount === 2) {
        const [p1, p2] = Array.from(pointersRef.current.values());
        isPinchingRef.current = true;
        isDraggingRef.current = false;
        setIsDragging(false);

        pinchStartDistRef.current = getDistance(p1, p2);
        pinchStartScaleRef.current = scale;
        pinchCenterRef.current = getMidpoint(p1, p2);
      }
    },
    [scale],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (isPinchingRef.current && pointersRef.current.size >= 2) {
        updatePointer(e);
        const pts = Array.from(pointersRef.current.values());
        if (pts.length < 2) return;

        const dist = getDistance(pts[0], pts[1]);
        if (pinchStartDistRef.current <= 0) return;

        const nextScale = clamp(
          (pinchStartScaleRef.current * dist) / pinchStartDistRef.current,
          MIN_SCALE,
          MAX_SCALE,
        );

        const center = pinchCenterRef.current;
        const scaleFactor = nextScale / scale;

        setOffset((prev) => ({
          x: center.x - (center.x - prev.x) * scaleFactor,
          y: center.y - (center.y - prev.y) * scaleFactor,
        }));
        setScale(nextScale);
        return;
      }

      if (!isPinchingRef.current && isDraggingRef.current && scale > 1) {
        const dx = e.clientX - lastPosRef.current.x;
        const dy = e.clientY - lastPosRef.current.y;
        lastPosRef.current = { x: e.clientX, y: e.clientY };
        setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      }
    },
    [scale],
  );

  const endGesture = useCallback((e) => {
    removePointer(e);
    if (pointersRef.current.size < 2) {
      isPinchingRef.current = false;
    }
    if (pointersRef.current.size === 0) {
      isDraggingRef.current = false;
      setIsDragging(false);
    }
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  }, []);

  if (!open) return null;

  // --- Logic for Header Values ---
  const renderHeaderContent = () => {
    if (type === "transform") {
      return (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="bg-green-100 text-green-800 px-4 py-1.5 rounded-full font-bold border border-green-200">
            สินค้า: {imageDetail?.productName || "-"}
          </span>
          <span className="bg-gray-100 text-gray-800 px-4 py-1.5 rounded-full font-bold border border-gray-200">
            น้ำหนัก: {formatNumber(imageDetail?.weight ?? 0)} kg
          </span>
          <span className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full font-bold border border-blue-100">
            รูปทั้งหมด: {images.length} รูป
          </span>
        </div>
      );
    }

    if (type === "car" || type === "img") {
      const heavy = imageDetail?.carHeavyWeight ?? 0;
      const light = imageDetail?.carLightWeight ?? 0;
      const net = heavy - light;
      return (
        <div className="flex items-center gap-2 text-sm">
          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
            รถหนัก: {formatNumber(heavy)}
          </span>
          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full font-medium">
            รถเบา: {formatNumber(light)}
          </span>
          <span className="bg-[#10B981] text-white px-3 py-1 rounded-full font-bold">
            น้ำหนักรถสุทธิ: {formatNumber(net)}
          </span>
        </div>
      );
    }

    // Product Mode
    const productName = imageDetail?.productName || "-";
    const weightCheck = imageDetail?.weight ?? 0;
    const deduct = imageDetail?.deductWeight ?? 0;

    const impurityWeight =
      (imageDetail?.impurityWeight || 0) +
      (imageDetail?.deductedItems?.reduce((a, c) => a + (c.weight || 0), 0) ||
        0);
    const totalDeduct = deduct;
    const net = imageDetail?.netWeight ?? 0;

    // Construct note string from impurities
    const combined = [
      ...(imageDetail?.impurities || []).map((item) => ({
        ...item,
        label: item.type || item.name || "",
        createdAt: item.createdAt,
        kind: item.kind || "discard",
        source: "impurity",
      })),
      ...(imageDetail?.deductedItems || []).map((item) => ({
        ...item,
        label: item.productName,
        createdAt: item.createdAt,
        kind: "return",
        source: "deducted",
      })),
    ];
    const sorted = combined.sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    );

    const kindLabelMap = {
      convert: "หักเป็นสินค้าอื่น",
      discard: "หักทิ้ง",
      return: "หักคืน",
    };

    const noteText = sorted.length
      ? sorted
        .map((item) => {
          console.log(item);

          // แปลง kind ตามเงื่อนไข
          const effectiveKind =
            item.kind === "return" && item.source === "deducted"
              ? "convert"
              : item.kind;

          const label = kindLabelMap[effectiveKind] ?? "";

          return `${label}${item.label ? `${item.label}` : ""}`;
        })
        .join(", ")
      : "";

    return (
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Item Badge (Green) */}
          <span className="bg-green-100 text-green-800 px-4 py-1.5 rounded-full font-bold text-sm border border-green-200">
            สินค้า: {productName}
          </span>
          {/* Total Badge (Gray/Blue) */}
          <span className="bg-gray-100 text-gray-800 px-4 py-1.5 rounded-full font-bold text-sm border border-gray-200">
            รวม: {formatNumber(weightCheck)} kg
          </span>
          {/* Deduction Badge (Red) */}
          <span className="bg-red-50 text-red-600 px-4 py-1.5 rounded-full font-bold text-sm border border-red-100">
            หัก: -{formatNumber(totalDeduct)} kg
          </span>
          {/* Impurity Badge (Orange) */}
          <span className="bg-orange-50 text-orange-600 px-4 py-1.5 rounded-full font-bold text-sm border border-orange-100">
            เจือปน: {formatNumber(impurityWeight)} kg
          </span>
          {/* Net Badge (Primary) */}
          <span className="bg-[#10B981] text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-sm">
            Net: {formatNumber(net)} kg
          </span>
        </div>

        {noteText && (
          <div className="flex items-center gap-1 text-xs text-gray-500 ml-1">
            <span className="font-semibold text-gray-700">Note:</span>
            <span>{noteText}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center">
      {/* Modal Card */}
      <div className="bg-white w-full h-full flex flex-col shadow-none overflow-hidden relative animate-fade-in">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
              <PhotoCameraIcon />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 leading-tight">
                {type === "transform"
                  ? imageDetail?.title || "รูปภาพการแปรสภาพ"
                  : type === "car" || type === "img"
                    ? "รูปภาพการชั่งน้ำหนักรถขนส่ง"
                    : "รูปภาพการชั่งน้ำหนัก"}
              </h2>
              <span className="text-xs text-gray-400 font-medium">
                {formatDateFull(current?.createdAt)}
              </span>
            </div>
          </div>

          <div className="hidden md:block">{renderHeaderContent()}</div>

          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Mobile Info (Visible only on small screens) */}
        <div className="md:hidden px-4 py-2 border-b border-gray-100 bg-gray-50">
          {renderHeaderContent()}
        </div>

        {/* Main Body */}
        <div className="flex-1 flex flex-col min-h-0 relative">
          {/* Main Image Area */}
          <div
            className="flex-1 bg-gray-900 relative flex items-center justify-center overflow-hidden group select-none"
            ref={containerRef}
            onWheel={handleWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endGesture}
            onPointerCancel={endGesture}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => e.preventDefault()}
            style={{ touchAction: "none" }}
          >
            {/* Navigation Arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute left-4 z-20 w-12 h-12 rounded-full bg-black/30 hover:bg-black/60 text-white/70 hover:text-white flex items-center justify-center backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
            >
              <ArrowBackIosIcon sx={{ fontSize: 20, ml: 1 }} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute right-4 z-20 w-12 h-12 rounded-full bg-black/30 hover:bg-black/60 text-white/70 hover:text-white flex items-center justify-center backdrop-blur-md transition-all opacity-0 group-hover:opacity-100"
            >
              <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
            </button>

            {/* Main Content */}
            {current ? (
              <img
                src={current.imageUrl}
                alt="Proof"
                className="object-contain max-w-full max-h-full transition-transform duration-100 ease-out"
                style={{
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  cursor:
                    scale > 1
                      ? isDragging
                        ? "grabbing"
                        : "default"
                      : "default",
                }}
                draggable={false}
              />
            ) : (
              <div className="text-gray-500 flex flex-col items-center gap-2">
                <PhotoCameraIcon sx={{ fontSize: 48, opacity: 0.5 }} />
                <span>No Image Selected</span>
              </div>
            )}

            {/* Info Overlay (Optional - currently using Header for info as requested) */}
          </div>

          {/* Thumbnails Strip */}
          <div className="h-32 bg-gray-50 border-t border-gray-200 p-3 flex gap-3 overflow-x-auto items-center shrink-0">
            {images.map((thumb) => {
              const isActive = current && thumb.id === current.id;
              return (
                <div
                  key={thumb.id}
                  onClick={() => setCurrent(thumb)}
                  className={`
                                relative flex-shrink-0 w-40 h-full rounded-xl overflow-hidden cursor-pointer transition-all duration-200
                                ${isActive ? "border-2 border-[#10B981] opacity-100 shadow-md transform scale-[1.02]" : "border border-transparent opacity-60 hover:opacity-100 hover:bg-gray-200"}
                            `}
                >
                  <img
                    src={thumb.imageUrl}
                    alt="thumb"
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  {/* Timestamp overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 pt-4">
                    <p className="text-white text-[10px] font-medium text-right">
                      {formatDate(thumb.createdAt)}
                    </p>
                  </div>

                  {/* Active Indicator Icon */}
                  {isActive && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-[#10B981] rounded-full flex items-center justify-center shadow-sm">
                      <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
